import React, {
	useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import type {
	MediaItem, PagedMediaItemExisting, PresentedMediaItem, VideoState,
} from '../../stores/types/media';
import { pageExists } from '../../utils/media';
import useMedia from '../../hooks/use-media';
import { ROOM_STATES } from '../../stores/reducers/settings';
import * as MediaActions from '../../stores/actions/media';
import * as keyListeners from '../../utils/keyboard-handler';
import useShotMemos from '../../hooks/use-shot-memos';
import { MediaControlsContext } from '../../contexts/media-controls';

type UseDeckControlsProps = {
  mediaId: number;
  isEditingFeed?: boolean;
  showbuilderMediaItem?: PresentedMediaItem;
  updateShowbuilderMedia: Function;
  onSelectMediaFeedChange: React.Dispatch<React.SetStateAction<number | undefined>>;
  isForShotEditorModal?: boolean;
};

type UseDeckControlsResult = {
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
};

const UseDeckControls = ({
	mediaId,
	isEditingFeed,
	showbuilderMediaItem,
	updateShowbuilderMedia,
	onSelectMediaFeedChange,
	isForShotEditorModal,
}: UseDeckControlsProps): UseDeckControlsResult => {
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const mediaPages = useSelector((state: GS) => state.MediaReducer.mediaPages);
	const shotStartTimestampEntity = useSelector(
		(state: GS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const mediaModalOpen = useSelector((state: GS) => state.MediaReducer.mediaModalOpen);
	const modalOpen = useSelector((state: GS) => state.ModalReducer.modalOpen);
	const forceFireSyncOnAutoplay = useRef<boolean>(false);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);
	const syncVidState = useMemo(
		() => presentedMediaItem?.videoState,
		[presentedMediaItem?.videoState],
	);
	const dispatch = useDispatch();
	const {
		currentShot,
	} = useShotMemos();
	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();
	const { addMediaControls } = useContext(MediaControlsContext);
	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);

	const shouldAutoPlay = useMemo((): boolean => {
		if (roomState === ROOM_STATES.EDITING || isEditingFeed) return false;

		// if the first shot has not started playing yet, this is null
		if (!shotStartTimestampEntity) return false;

		// if syncVidState.paused is true, has been synced and paused previously
		// if syncVidState is undefined, this is initial load/sync for the room
		if (syncVidState?.paused === true) return false;

		return true;
	}, [roomState, shotStartTimestampEntity, syncVidState]);

	const [userCanControl, userCanDelete] = useMemo(() => [
		mediaItem ? canEditOrControlMedia(mediaItem, user, host, true, roomState) : false,
		mediaItem ? canDeleteMedia(mediaItem, user, host) : false,
	], [host, mediaItem, user, currentShot]);

	const changePage = useCallback((newPage: number) => {
		console.log('feed-media.tsx changePage', { mediaItem, presentedMediaItem, newPage });

		if (!mediaItem) return;
		const newPageUrl = pageExists(newPage, mediaItem);

		if (
			!presentedMediaItem
        || !canEditOrControlMedia(mediaItem, user, host, true)
        || !newPageUrl
		) return;

		// on pagenation, video state is not cleared. We force write new state.
		forceFireSyncOnAutoplay.current = true;

		const newMediaPage = mediaPages.find((page: PagedMediaItemExisting) => page.url === newPageUrl);
		const newVideoState: VideoState = {
			paused: !shouldAutoPlay,
			timeElapsed: 0,
			volume: 1,
		};

		if (newMediaPage) {
			newVideoState.volume = newMediaPage.volume;
			newVideoState.muted = newMediaPage.muted;
		}

		if (isEditingFeed) {
			updateShowbuilderMedia(
				{ ...presentedMediaItem, currentPage: newPage, videoState: newVideoState },
			);
		} else {
			dispatch(MediaActions.setPresentedMediaPage(mediaId, newPage, newVideoState));
		}

		onSelectMediaFeedChange(mediaId);
	}, [
		mediaItem,
		host,
		presentedMediaItem,
		user,
		mediaId,
		shouldAutoPlay,
		isForShotEditorModal,
		showbuilderMediaItem,
		mediaPages,
	]);

	const nextPage = () => {
		if (
			!mediaModalOpen
        && (!modalOpen || isForShotEditorModal)
        && (userCanControl || userCanDelete)) {
			changePage((presentedMediaItem?.currentPage ?? 1) + 1);
		}
	};

	const previousPage = () => {
		if (
			!mediaModalOpen
        && (!modalOpen || isForShotEditorModal)
        && (userCanControl || userCanDelete)) {
			changePage((presentedMediaItem?.currentPage ?? 1) - 1);
		}
	};

	const goToPage = (page: number) => {
		if (
			!mediaModalOpen
        && (!modalOpen || isForShotEditorModal)
        && (userCanControl || userCanDelete)) {
			changePage(page);
		}
	};

	useEffect(() => {
		if (presentedMediaItem && mediaItem && mediaItem.num_pages > 1) {
			keyListeners.addListener(keyListeners.KEYS.KEY_LEFT, previousPage);
			keyListeners.addListener(keyListeners.KEYS.KEY_RIGHT, nextPage);
			keyListeners.addListener(keyListeners.KEYS.KEY_UP, previousPage);
			keyListeners.addListener(keyListeners.KEYS.KEY_DOWN, nextPage);
		}
		return () => {
			keyListeners.removeListener(keyListeners.KEYS.KEY_LEFT, previousPage);
			keyListeners.removeListener(keyListeners.KEYS.KEY_RIGHT, nextPage);
			keyListeners.addListener(keyListeners.KEYS.KEY_UP, previousPage);
			keyListeners.addListener(keyListeners.KEYS.KEY_DOWN, nextPage);
		};
	}, [
		modalOpen,
		isForShotEditorModal,
		mediaModalOpen,
		userCanControl,
		userCanDelete,
		presentedMediaItem,
	]);

	useEffect(() => {
		if (presentedMediaItem && (mediaItem?.num_pages ?? 0) > 1) {
			addMediaControls({
				id: `${mediaId}`,
				state: { isForShotEditorModal },
				nextPage,
				previousPage,
				goToPage,
				from: 'useDeckControls',
			});
		}
	}, [presentedMediaItem?.currentPage, mediaItem?.num_pages, mediaId]);

	return {
		goToPage,
		nextPage,
		previousPage,
	};
};

export default UseDeckControls;
